import styles from './template.css';
import template from './template';
import {property} from 'lit-element';
import {AoflElement, customElement} from '@aofl/element';
import {i18nMixin} from '@aofl/i18n-mixin';
import translations from '@modules/i18n';
import {videosHandlerInstance} from '@modules/videos-handler-instance';
import {lessonDetailSections, products} from '@modules/constants-enumerate';
import {query} from 'lit-element';
import {ApiService as ComponentsApiService} from '@epg/epg-components';

/**
 * @summary AcademyLessonDetails
 * @extends {AoflElement}
 */
@customElement('academy-lesson-details')
class AcademyLessonDetails extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of AcademyLessonDetails.
   */
  constructor() {
    super();
    this.translations = translations;
  }

  @property({type: Object, attribute: false})
  videoWrapper = {
    'position-relative': true,
    'd-flex': true,
    'justify-content-center': true,
    'overflow-hidden': true,
    'video-active': false
  }

  @property({type: Object})
  skill = {};

  @query('video')
  video = null;

  @property({type: Boolean})
  forModal = true;

  @property({type: String})
  product = '';

  @property({type: Array})
  activityIds = [];

  @property({type: String})
  demoToken = '';

  @property({type: Boolean})
  activitiesLoading = false;

  _lastActivitiesSkillId = null;

  _activitiesRequestId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.videosHandler = videosHandlerInstance;
    if (!this.skill?.video) {
      return;
    }
    setTimeout(() => {
      if (this.skill.video_thumbnail) {
        this.video?.setAttribute('poster', 'https://educate.aofl.com/cdn' + this.skill.video_thumbnail);
      } else {
        this.video?.addEventListener('loadedmetadata', () => {
          this.video.currentTime = 0;
          this.video.pause();
          const canvas = document.createElement('canvas');
          canvas.width = this.video.parentElement.clientWidth;
          canvas.height = this.video.parentElement.clientHeight;
          const context = canvas.getContext('2d');
          context.drawImage(this.video, 0, 0, canvas.width, canvas.height);
          const posterDataUrl = canvas.toDataURL();
          this.video.setAttribute('poster', posterDataUrl);
        });
      }
    });
  }

  updated(changedProperties) {
    if (changedProperties.has('product')) {
      this.demoToken = '';
      this._lastActivitiesSkillId = null;
    }
    if (changedProperties.has('skill') || changedProperties.has('product')) {
      this.loadSkillActivities();
    }
  }

  resolveProduct() {
    return this.product || new URLSearchParams(window.location.search).get('product') || '';
  }

  isActivityDemoEnabled() {
    return ComponentsApiService.isMasteryDemoActivitiesEnabled(this.resolveProduct());
  }

  getActivityThumbnailUrl(activityId) {
    return ComponentsApiService.getActivityThumbnailUrl(this.resolveProduct(), activityId);
  }

  getGenericActivityThumbnailUrl() {
    const genericThumbnails = {
      [products.MY_MATH_ACADEMY]: require('@assets/images/my_math_academy/app-icon.webp'),
      [products.MY_READING_ACADEMY]: require('@assets/images/my_reading_academy/app-icon.webp'),
    };
    return genericThumbnails[this.resolveProduct()] || '';
  }

  getActivityDemoUrl(activityId) {
    return ComponentsApiService.buildMasteryDemoActivityUrl(
      this.resolveProduct(),
      activityId,
      this.demoToken
    );
  }

  async loadSkillActivities() {
    const skillId = this.skill?.id;
    if (!skillId) {
      this._lastActivitiesSkillId = null;
      this.activityIds = [];
      this.demoToken = '';
      this.activitiesLoading = false;
      return;
    }
    const product = this.resolveProduct();
    const loadKey = `${skillId}:${product}`;
    if (this._lastActivitiesSkillId === loadKey) {
      return;
    }
    this._lastActivitiesSkillId = loadKey;
    if (!ComponentsApiService.isMasteryDemoActivitiesEnabled(product)) {
      this.activityIds = [];
      this.demoToken = '';
      this.activitiesLoading = false;
      return;
    }
    const requestId = ++this._activitiesRequestId;
    this.activitiesLoading = true;
    this.activityIds = [];
    try {
      const [activityIds, auth] = await Promise.all([
        ComponentsApiService.masteryDemoGetSkillActivities(skillId, product),
        ComponentsApiService.masteryDemoGetAuthorization(),
      ]);
      if (requestId !== this._activitiesRequestId) {
        return;
      }
      this.activityIds = activityIds;
      this.demoToken = auth?.token || '';
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      if (requestId !== this._activitiesRequestId) {
        return;
      }
      this.activityIds = [];
      this.demoToken = '';
      this._lastActivitiesSkillId = null;
    } finally {
      if (requestId === this._activitiesRequestId) {
        this.activitiesLoading = false;
      }
    }
  }

  firstUpdated() {
    this.videosHandler?.addVideoToMap({
      id: this.skill?.id,
      video: this.video
    });
    this.video?.addEventListener('play', () => {
      this.videosHandler.pauseAllVideosExcept(this.skill?.id);
    });
    this.video?.addEventListener('ended', () => {
      this.setVideoImageActive(false);
    });
  }

  toggleVideo() {
    this.setVideoImageActive(true);
    this.video.paused ? this.video.play() : this.video.pause();
  }

  viewWorksheet(worksheetDetails) {
    this.dispatchEvent(new CustomEvent('on-viewchange', {
      detail: {
        view: lessonDetailSections.PDF,
        details: worksheetDetails
      },
      composed: true,
      bubbles: true
    }));
  }

  setVideoImageActive(isActive) {
    this.videoWrapper = {
      ...this.videoWrapper,
      'video-active': isActive
    };
  }

  viewRecommendedActivities() {
    this.dispatchEvent(new CustomEvent('on-viewchange', {
      detail: {
        view: lessonDetailSections.RECOMMENDED_ACTIVITY,
        recommendedActivities: this.skill.recommended_activities,
        skillName: this.skill.name,
      },
      composed: true,
      bubbles: true
    }));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.videosHandler?.removeVideoFromMap(this.skill?.id);
    if (!this.video) return;
    this.removeEventListener('play', this.video);
    this.removeEventListener('ended', this.video);
    this.removeEventListener('loadedmetadata', this.video);
  }

  /**
   * @readonly
   */
  static is = 'academy-lesson-details';
  /**
   * @return {Object}
   */
  render() {
    return super.render({
      default: {template, styles: [styles]}
    });
  }
}

export default AcademyLessonDetails;
